<script setup lang="ts">
import DefaultLayout from "@/components/layouts/DefaultLayout.vue";
import { computed, onMounted, ref } from "vue";
import { gql } from "@apollo/client/core";
import { useMutation } from "@vue/apollo-composable";
import { getMessageFromError } from "@/libs/errors";
import { platform } from "@/libs/environment";
import { useCordovaStore } from "@/stores/cordova";
import UserArea from "../../components/ui/UserArea.vue";

const cordovaStore = useCordovaStore();
const loading = ref(false);
const errorMessage = ref("");
const successMessage = ref("");

// Assinaturas compradas no Google Play só podem ser canceladas lá: o app apenas
// recebe o evento de cancelamento e processa a baixa internamente.
const isGooglePlaySubscription = computed(
  () => cordovaStore.hasGooglePlaySubscription
);
const checkingStore = computed(
  () => platform !== "web" && !cordovaStore.storeReady
);

onMounted(() => {
  if (platform === "web") {
    return;
  }
  cordovaStore.initCordovaPluginPurchase();
  cordovaStore.refreshStore();
});

// GraphQL mutation for canceling subscription
const { mutate: cancelSubscription } = useMutation(
  gql`
    mutation CancelSubscription {
      cancelSubscription
    }
  `,
  {
    fetchPolicy: "no-cache",
  }
);

const submit = async () => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  errorMessage.value = "";
  successMessage.value = "";
  try {
    await cancelSubscription();
    successMessage.value = "Assinatura cancelada com sucesso.";
    // Opcional: tomar ações adicionais, como atualizar store, redirecionar, etc
  } catch (err) {
    errorMessage.value = getMessageFromError(err);
  }
  loading.value = false;
};
</script>

<template>
  <DefaultLayout>
    <article class="container framework-slot">
      <div class="row">
        <div class="col">
          <UserArea title="Cancelar Assinatura">
            <div v-if="checkingStore" class="row">
              <div class="col-md-12 mb-3">Verificando sua assinatura...</div>
            </div>

            <div v-else-if="isGooglePlaySubscription" class="row">
              <div class="col-md">
                <div class="alert alert-info">
                  Sua assinatura foi contratada pelo Google Play. Por isso, o
                  cancelamento precisa ser feito no próprio Google Play — o
                  Quanto Faz não consegue cancelá-la por aqui.
                </div>
                <p>
                  Assim que o Google confirmar o cancelamento, damos baixa
                  automaticamente na sua assinatura. Você continua com acesso
                  aos benefícios até o fim do período já pago.
                </p>
                <button
                  class="btn btn-submit btn-primary mb-3"
                  type="button"
                  @click="cordovaStore.manageSubscriptions()"
                >
                  Cancelar no Google Play
                </button>
                <div class="profileContentNav">
                  <p class="mb-1">Se o botão acima não abrir, faça assim:</p>
                  <ol class="mb-0">
                    <li>Abra o app <strong>Google Play</strong>.</li>
                    <li>
                      Toque na sua foto de perfil e escolha
                      <strong>Pagamentos e assinaturas</strong> &rsaquo;
                      <strong>Assinaturas</strong>.
                    </li>
                    <li>Selecione <strong>Quanto Faz?</strong>.</li>
                    <li>Toque em <strong>Cancelar assinatura</strong>.</li>
                  </ol>
                </div>
              </div>
            </div>

            <form v-else @submit.prevent="submit">
              <div v-if="errorMessage" class="alert alert-danger">
                {{ errorMessage }}
              </div>
              <div v-if="successMessage" class="alert alert-success">
                {{ successMessage }}
              </div>
              <div class="row">
                <div class="col-md">
                  <div class="form-group mb-3">
                    <label class="form-label">
                      Tem certeza que deseja cancelar sua assinatura?
                    </label>
                    <div class="profileContentNav">
                      <p>
                        Ao cancelar sua assinatura, você perderá acesso aos
                        benefícios exclusivos de assinante.
                      </p>
                    </div>
                  </div>
                  <button
                    v-if="loading"
                    class="btn btn-danger mb-3"
                    type="submit"
                    disabled
                  >
                    Cancelando...
                  </button>
                  <button
                    v-else
                    class="btn btn-danger mb-3"
                    type="submit"
                    :disabled="!!successMessage"
                  >
                    Cancelar Assinatura
                  </button>
                </div>
              </div>
            </form>
          </UserArea>
        </div>
      </div>
    </article>
  </DefaultLayout>
</template>

<style scoped></style>
